import React, { useState, useEffect } from 'react';
import { Search, Globe, TrendingUp, AlertCircle, CheckCircle, ArrowRight, Zap, BarChart3, ExternalLink } from 'lucide-react';

interface ScanResult {
  domain: string;
  dr_score: number;
  organic_traffic: number;
  backlinks: number;
  referring_domains: number;
  organic_keywords: number;
  issues: Array<{
    type: 'critical' | 'warning' | 'info';
    title: string;
    description: string;
    impact: string;
  }>;
  suggestions: Array<{
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    estimated_impact: string;
  }>;
  citation_opportunities: number;
  estimated_improvement: {
    traffic_increase: string;
    ranking_boost: string;
    authority_gain: number;
  };
}

const DomainScanner: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState('');
  const [isAutoScanning, setIsAutoScanning] = useState(false);

  // Handle URL parameters for domain and auto-scan
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const domainParam = urlParams.get('domain');
    const autoScanParam = urlParams.get('autoScan');
    
    if (domainParam) {
      setDomain(domainParam);
      // If autoScan=true, automatically trigger scan
      if (autoScanParam === 'true') {
        setIsAutoScanning(true);
        // Small delay to ensure component is fully mounted
        setTimeout(() => {
          handleScan(domainParam);
        }, 100);
      }
    }
  }, []);

  const handleScan = async (domainToScan?: string) => {
    const targetDomain = domainToScan || domain;
    if (!targetDomain) {
      setError('Please enter a domain to scan');
      return;
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    const cleanDomain = targetDomain.replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    if (!domainRegex.test(cleanDomain)) {
      setError('Please enter a valid domain (e.g., example.com)');
      return;
    }

    setIsScanning(true);
    setIsAutoScanning(false); // Clear auto-scanning state when manual scan starts
    setError('');

    try {
      // Step 1: Get basic data immediately
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/scanner/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: cleanDomain }),
      });

      if (!response.ok) {
        throw new Error('Failed to scan domain');
      }

      const result = await response.json();
      if (result.success) {
        setScanResult(result.data);
        setIsScanning(false); // Stop loading for basic data
        
        // Step 2: Get detailed data in background
        setTimeout(async () => {
          try {
            const detailedResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/scanner/detailed/${encodeURIComponent(cleanDomain)}`);
            const detailedResult = await detailedResponse.json();
            
            if (detailedResult.success) {
              setScanResult(prev => ({
                ...prev,
                ...detailedResult.data,
                loading: detailedResult.data.loading
              }));
            }
          } catch (err) {
            console.error('Failed to load detailed data:', err);
          }
        }, 1000); // Wait 1 second before requesting detailed data
        
      } else {
        setError(result.error || 'Failed to analyze domain');
        setIsScanning(false);
      }
    } catch (err) {
      setError('Unable to scan domain. Please try again.');
      console.error('Scan error:', err);
      setIsScanning(false);
    }
  };

  const handleOrderCitations = () => {
    if (scanResult) {
      // Navigate to order page with scan data
      window.location.href = `/order?domain=${scanResult.domain}&scan=true`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text">BIP SEO</h1>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/network" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Our Network
              </a>
              <a href="/login" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Login
              </a>
              <a href="/register" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Free SEO Domain Scanner
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Get instant SEO analysis powered by Ahrefs data and AI recommendations. 
            Discover citation opportunities across our 301-domain network.
          </p>
        </div>

        {/* Auto-scanning indicator */}
        {isAutoScanning && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-lg font-semibold text-blue-800">Auto-scanning from homepage...</span>
            </div>
            <p className="text-blue-600">Analyzing <strong>{domain}</strong> with real Ahrefs data</p>
          </div>
        )}

        {/* Scanner Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Enter your domain (e.g., example.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleScan()}
                className="w-full pl-10 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                disabled={isScanning}
              />
            </div>
            <button
              onClick={handleScan}
              disabled={isScanning}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center gap-2"
            >
              {isScanning ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Scan Domain
                </>
              )}
            </button>
          </div>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}
        </div>

        {/* Scan Results */}
        {scanResult && (
          <div className="space-y-8">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Domain Rating</span>
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{scanResult.dr_score}</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${(scanResult.dr_score / 100) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Organic Traffic</span>
                  <TrendingUp className="w-4 h-4 text-green-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{scanResult.organic_traffic.toLocaleString()}</div>
                <div className="text-sm text-gray-500">monthly visits</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Backlinks</span>
                  <Globe className="w-4 h-4 text-purple-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{scanResult.backlinks.toLocaleString()}</div>
                <div className="text-sm text-gray-500">total links</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Keywords</span>
                  <Zap className="w-4 h-4 text-orange-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{scanResult.organic_keywords.toLocaleString()}</div>
                <div className="text-sm text-gray-500">ranking</div>
              </div>
            </div>

            {/* Data Source Indicator */}
            {scanResult.data_source && (
              <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-3">
                  {scanResult.data_source === 'ahrefs_real' ? (
                    <>
                      <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <span className="text-green-700 font-semibold">✅ Real Ahrefs Data</span>
                        <span className="text-green-600 text-sm ml-2">- Live SEO metrics from Ahrefs API</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <span className="text-yellow-700 font-semibold">⚠️ Estimated Data</span>
                        <span className="text-yellow-600 text-sm ml-2">- Based on industry averages</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Top Pages & Backlinks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Top Pages */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  Top Performing Pages
                </h3>
                <div className="space-y-3">
                  {scanResult.top_pages && scanResult.top_pages.length > 0 ? (
                    scanResult.top_pages.slice(0, 5).map((page: any, index: number) => (
                      <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {page.url || 'URL not available'}
                            </p>
                            {page.traffic && (
                              <p className="text-xs text-blue-600">
                                {page.traffic.toLocaleString()} monthly visits
                              </p>
                            )}
                          </div>
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p>No top pages data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Backlinks */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Recent Backlinks
                </h3>
                <div className="space-y-3">
                  {scanResult.backlinks_list && scanResult.backlinks_list.length > 0 ? (
                    scanResult.backlinks_list.slice(0, 5).map((backlink: any, index: number) => (
                      <div key={index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {backlink.url_from || 'Domain not available'}
                            </p>
                            {backlink.domain_rating && (
                              <p className="text-xs text-green-600">
                                DR: {backlink.domain_rating}
                              </p>
                            )}
                          </div>
                          <a
                            href={backlink.url_from}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-green-600 hover:text-green-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p>No recent backlinks data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* AI Analysis */}
            {scanResult.loading?.ai_analysis ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-500" />
                  AI-Powered SEO Analysis
                </h3>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Generating AI insights...</span>
                </div>
              </div>
            ) : scanResult.ai_analysis ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-500" />
                  AI-Powered SEO Analysis
                </h3>
                <div className="space-y-4">
                  {typeof scanResult.ai_analysis === 'string' ? (
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {scanResult.ai_analysis}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {scanResult.ai_analysis.seo_health_score && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-semibold text-green-800 mb-2">SEO Health Score</h4>
                          <p className="text-green-700">{scanResult.ai_analysis.seo_health_score}</p>
                        </div>
                      )}
                      {scanResult.ai_analysis.critical_issues && (
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h4 className="font-semibold text-red-800 mb-2">Critical Issues</h4>
                          <p className="text-red-700">{scanResult.ai_analysis.critical_issues}</p>
                        </div>
                      )}
                      {scanResult.ai_analysis.opportunity_analysis && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-800 mb-2">Opportunity Analysis</h4>
                          <p className="text-blue-700">{scanResult.ai_analysis.opportunity_analysis}</p>
                        </div>
                      )}
                      {scanResult.ai_analysis.content_strategy && (
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-semibold text-purple-800 mb-2">Content Strategy</h4>
                          <p className="text-purple-700">{scanResult.ai_analysis.content_strategy}</p>
                        </div>
                      )}
                      {scanResult.ai_analysis.link_building_strategy && (
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <h4 className="font-semibold text-orange-800 mb-2">Link Building Strategy</h4>
                          <p className="text-orange-700">{scanResult.ai_analysis.link_building_strategy}</p>
                        </div>
                      )}
                      {scanResult.ai_analysis.action_plan && (
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                          <h4 className="font-semibold text-indigo-800 mb-2">90-Day Action Plan</h4>
                          <p className="text-indigo-700">{scanResult.ai_analysis.action_plan}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Competitor Gap Analysis */}
            {scanResult.loading?.competitor_analysis ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  Competitor Gap Analysis & Keyword Opportunities
                </h3>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Analyzing competitors and keywords...</span>
                </div>
              </div>
            ) : scanResult.competitor_gap_analysis ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  Competitor Gap Analysis & Keyword Opportunities
                </h3>
                
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Choose which keywords you'd like to publish content for across our network of 301+ high-authority domains.
                  </p>
                  
                  {/* Keyword Selection */}
                  <div className="space-y-3">
                    {scanResult.competitor_gap_analysis.low_hanging_fruit && scanResult.competitor_gap_analysis.low_hanging_fruit.length > 0 ? (
                      scanResult.competitor_gap_analysis.low_hanging_fruit.slice(0, 10).map((keyword: any, index: number) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id={`keyword-${index}`}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                defaultChecked={index < 3} // Auto-select first 3 keywords
                              />
                              <label htmlFor={`keyword-${index}`} className="flex-1 cursor-pointer">
                                <div>
                                  <p className="font-medium text-gray-900">{keyword.keyword}</p>
                                  <p className="text-sm text-gray-600">
                                    Position: {keyword.position} | Traffic: {keyword.traffic?.toLocaleString() || 'N/A'} | Opportunity Score: {keyword.opportunity_score}
                                  </p>
                                </div>
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                                Low Hanging Fruit
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                                Score: {keyword.opportunity_score}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No keywords available for selection</p>
                    )}
                  </div>
                </div>

                {/* Publishing Options */}
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-4">Publishing Options</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">What You'll Get:</h5>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• AI-generated, SEO-optimized content</li>
                        <li>• Custom featured images for each post</li>
                        <li>• Published across selected high-authority domains</li>
                        <li>• Backlinks to your target URL</li>
                        <li>• Detailed reporting and tracking</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Expected Results:</h5>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Improved keyword rankings</li>
                        <li>• Increased organic traffic</li>
                        <li>• Enhanced domain authority</li>
                        <li>• Better search visibility</li>
                        <li>• Professional backlink profile</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Opportunity Summary */}
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {scanResult.competitor_gap_analysis.total_opportunities || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Opportunities</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {scanResult.competitor_gap_analysis.estimated_traffic_gain?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">Est. Traffic Gain</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${scanResult.competitor_gap_analysis.estimated_value_gain?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">Est. Value Gain</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {scanResult.competitor_gap_analysis.low_hanging_fruit?.length || 0}
                      </div>
                      <div className="text-sm text-gray-600">Quick Wins</div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => {
                        const selectedKeywords = scanResult.competitor_gap_analysis.low_hanging_fruit
                          .slice(0, 10)
                          .filter((_, index) => {
                            const checkbox = document.getElementById(`keyword-${index}`) as HTMLInputElement;
                            return checkbox?.checked;
                          });
                        
                        if (selectedKeywords.length === 0) {
                          alert('Please select at least one keyword to continue.');
                          return;
                        }
                        
                        // Store selected keywords and redirect to order page
                        localStorage.setItem('keywordOrderData', JSON.stringify({
                          domain: scanResult.domain,
                          keywords: selectedKeywords,
                          seoData: scanResult
                        }));
                        
                        window.location.href = '/order?type=keywords';
                      }}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Publish Selected Keywords
                    </button>
                    <button
                      onClick={() => {
                        // Select all keywords
                        scanResult.competitor_gap_analysis.low_hanging_fruit
                          .slice(0, 10)
                          .forEach((_, index) => {
                            const checkbox = document.getElementById(`keyword-${index}`) as HTMLInputElement;
                            if (checkbox) checkbox.checked = true;
                          });
                      }}
                      className="px-6 py-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                    >
                      Select All
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Issues & Opportunities */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Issues */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  SEO Issues Found
                </h3>
                <div className="space-y-4">
                  {scanResult.issues && scanResult.issues.length > 0 ? (
                    scanResult.issues.map((issue, index) => (
                      <div key={index} className={`p-4 rounded-lg border-l-4 ${
                        issue.type === 'critical' ? 'bg-red-50 border-red-500' :
                        issue.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                        'bg-blue-50 border-blue-500'
                      }`}>
                        <h4 className="font-semibold text-gray-900">{issue.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{issue.description}</p>
                        <p className="text-xs text-gray-500 mt-2">Impact: {issue.impact}</p>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-600">No critical issues found. Your domain looks good!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Suggestions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  AI Recommendations
                </h3>
                <div className="space-y-4">
                  {scanResult.suggestions && scanResult.suggestions.length > 0 ? (
                    scanResult.suggestions.map((suggestion, index) => (
                      <div key={index} className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                        <div className="flex items-start justify-between">
                          <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            suggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                            suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {suggestion.priority}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                        <p className="text-xs text-green-600 mt-2 font-medium">
                          Estimated Impact: {suggestion.estimated_impact}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-600">No specific recommendations available at this time.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Citation Opportunity */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Citation Network Opportunity</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-300" />
                      <span>Found {scanResult.citation_opportunities} citation opportunities</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-300" />
                      <span>Potential {scanResult.estimated_improvement.traffic_increase} traffic increase</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-300" />
                      <span>Expected {scanResult.estimated_improvement.ranking_boost} ranking improvement</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-300" />
                      <span>+{scanResult.estimated_improvement.authority_gain} DR score boost</span>
                    </div>
                  </div>
                </div>
                <div className="text-center lg:text-right">
                  <div className="mb-4">
                    <div className="text-3xl font-bold mb-2">301 Citation Domains</div>
                    <div className="text-blue-200">Ready to boost your rankings</div>
                  </div>
                  <button
                    onClick={handleOrderCitations}
                    className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 mx-auto lg:mx-0"
                  >
                    Order Citations Now
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        {!scanResult && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comprehensive Analysis</h3>
              <p className="text-gray-600">
                Get detailed SEO insights powered by Ahrefs data and AI analysis
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Actionable Recommendations</h3>
              <p className="text-gray-600">
                Receive prioritized suggestions to improve your search rankings
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Citation Opportunities</h3>
              <p className="text-gray-600">
                Discover how our 301-domain network can boost your authority
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainScanner;
